import React, { useContext, useMemo } from 'react';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { vehicleContext } from '../VehicleProvider';
import Collapse from '../Collapse';
import Tabs from '../Tabs';
import { Container, SectionTitle, Wrapper } from './ContentWrapper.styles';
import { Headline, Layout, Skeleton } from '@oneaudi/unified-web-components';
import { SYS_SIZE_FIXED_6000 } from '@oneaudi/unified-web-common';
import useStockCar from '../../hooks/useStockCar';

interface ContentWrapperProps {
  market: string;
  isTier3: boolean;
  isMock: boolean;
  vehicleId?: string;
  oneGraphClient: ApolloClient<NormalizedCacheObject>;
}

const ContentWrapper: React.FC<ContentWrapperProps> = ({
  vehicleId,
  market,
  isTier3,
  isMock,
  oneGraphClient,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { loading, vehicle } = useContext(vehicleContext);
  const { vehicle: stockCarVehicle } = useStockCar({
    market,
    vehicleId,
    isTier3,
    isMock,
    oneGraphClient,
  });

  const renderTabComponent = useMemo(
    () => (
      <Container>
        {loading ? (
          <Skeleton data-testid="loading" variant="rectangular" height={SYS_SIZE_FIXED_6000} />
        ) : (
          <Tabs
            market={market}
            vehicle={vehicle}
            dynamicAttributes={stockCarVehicle?.dynamicAttributes}
          />
        )}
      </Container>
    ),
    [loading, market, stockCarVehicle?.dynamicAttributes, vehicle],
  );

  const renderContent = useMemo(() => {
    if (isMobile)
      return (
        <Collapse heading={t('featuresAndSpecsSection.heading')}>{renderTabComponent}</Collapse>
      );
    return (
      <SectionTitle>
        <Headline
          tag="h2"
          variant={{
            order: '1',
            type: 'headline',
            weight: 'bold',
          }}
        >
          {t('featuresAndSpecsSection.heading')}
        </Headline>
        {renderTabComponent}
      </SectionTitle>
    );
  }, [isMobile, renderTabComponent, t]);

  return loading || vehicle ? (
    <Wrapper showAccordian>
      <Layout direction="column" data-testid="feature-specs-wrapper">
        {renderContent}
      </Layout>
    </Wrapper>
  ) : null;
};

export default ContentWrapper;
